/**
 * Read hooks for the stablecoin invoicing console (SPEC-IVC, ALL-5908).
 *
 * One hook per read a page needs, each built only on the functions in
 * `src/features/invoicing/api.ts` (which is itself the only place that talks to
 * the wire). Nothing here mutates: every write is a page call to `api.ts`
 * directly, because a mutation is an event with an `Idempotency-Key`, not a
 * piece of state that a re-render may replay.
 *
 * ## The shape every hook returns
 *
 * `{data, loading, error, refresh}` — the house shape of
 * `src/hooks/use-hot-wallet-deposits.ts`. Two rules from that hook are kept
 * exactly, and for the same reasons:
 *
 *   - **Never throw.** A refusal, a 404, a body that is not JSON and a dropped
 *     connection all come back as `error` with the contract's reason code. A
 *     hook that threw would take the page down instead of letting it render the
 *     failure state it has copy for.
 *   - **Never invent data.** On any failure `data` is emptied (or null), never
 *     left holding the previous query's rows and never backfilled. This is the
 *     defect the harness paid for twice: a page showing $48.5M of assets that
 *     did not exist because an error was replaced with fixtures, and an empty
 *     state that was only an empty state because the call behind it had 401'd
 *     (page-delivery-resources gotchas 36, 111).
 *
 * ## The out-of-order guard
 *
 * Every hook bumps a `generation` ref before a load and drops any response
 * whose generation is no longer current. Without it, changing a filter (or an
 * id) while the previous read is still in flight lets the slower, older
 * response land last — so the page renders the previous filter's rows under the
 * new filter's heading. That is not staleness; it is the wrong customer's data
 * on screen, and nothing else in the stack catches it.
 *
 * ## Paging
 *
 * `useInvoices` and `useInvoiceExceptions` own their cursor: they append the
 * next page to `data` and expose `hasMore` / `loadMore` / `loadingMore` /
 * `loadMoreError`, matching `src/hooks/use-transfers.ts`. A filter change
 * restarts from page 1, because the gateway issues a cursor for the filter set
 * it was given (`PAGE_TOKEN_MISMATCH` when that is violated). The caller's
 * filter object deliberately cannot carry a `cursor` — page 2 is not a filter.
 *
 * ## What this module could not build, and why (R12)
 *
 * `useInvoiceResolutions` reads a resolution through
 * `GET /v1/invoice-exceptions/{id}`, whose `ExceptionDetail` carries the
 * exception's `resolutions`. The approval page spec
 * (`product-design/page-specs/invoicing.page-specs.json`,
 * `invoice-resolution-approval`) names `GET /v1/invoice-resolutions` as its
 * backend contract, and that list route has **no client function** in the
 * merged `api.ts` (31 operations; resolutions are write-only there: propose,
 * approve, reject, cancel). Reading through the exception detail is therefore
 * the only honest path today — it is real data, not a shape this module
 * invented. When a resolutions list route lands in `api.ts`, this hook changes
 * to it and the approval queue can page.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiErrorInfo } from "@/lib/api";
import {
  NETWORK_ERROR_CODE,
  getInvoice,
  getInvoiceException,
  getInvoiceJournal,
  getInvoicingSettings,
  listInvoiceExceptions,
  listInvoicePayments,
  listInvoices,
  type ApiPage,
} from "@/features/invoicing/api";
import type {
  Exception,
  ExceptionListFilters,
  Invoice,
  InvoiceListFilters,
  InvoiceSummary,
  InvoicingSettings,
  JournalEntry,
  Payment,
  Resolution,
} from "@/features/invoicing/types";

/** The four fields every read hook in this module returns. */
export interface InvoicingQueryResult<T> {
  data: T;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

/** A read that accumulates cursor pages as the caller asks for them. */
export interface InvoicingPageResult<T> extends InvoicingQueryResult<T[]> {
  /** The service issued another cursor; `loadMore` has something to read. */
  hasMore: boolean;
  loadingMore: boolean;
  /**
   * A failed `loadMore` only. The rows already loaded stay on screen: a page
   * that has page 1 must not lose it because page 2 was refused.
   */
  loadMoreError: ApiErrorInfo | null;
  loadMore: () => void;
}

/* ------------------------------------------------------------ the engine */

/**
 * The cursor-paged read both list hooks share.
 *
 * `fetchPage` and `keyOf` must keep a stable identity (`useCallback` and a
 * module-level function respectively): they are dependencies of the effect that
 * runs the read, so an inline lambda would make `load` change on every render
 * and re-read the API forever — the loop `use-api.ts` documents.
 *
 * `keyOf` de-duplicates across pages, so a row the service returns twice (a
 * cursor that overlaps by one row, which services do) is rendered once. It is
 * row identity, never a heuristic on the data.
 */
function useCursorPage<T>(
  fetchPage: (cursor: string | null) => Promise<ApiPage<T>>,
  keyOf: (row: T) => string,
): InvoicingPageResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<ApiErrorInfo | null>(null);
  // Bumped by every first-page load; see the module header.
  const generation = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    // A first-page load supersedes a `loadMore` in flight, so it must also take
    // over that call's state. Leaving `loadingMore` true here would disable the
    // button for good after a refresh raced a paging read.
    setLoadingMore(false);
    setLoadMoreError(null);
    try {
      const page = await fetchPage(null);
      if (gen !== generation.current) return;
      if (!page.ok) {
        setData([]);
        setNextCursor(null);
        setError(page.error);
      } else {
        setData(page.items);
        setNextCursor(page.nextCursor);
      }
    } catch {
      if (gen !== generation.current) return;
      setData([]);
      setNextCursor(null);
      setError({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    // Only a fresh page-1 read can change the cursor, so this guard cannot
    // strand a caller: `null` means the service said there is nothing more.
    if (nextCursor === null || loadingMore) return;
    const gen = generation.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await fetchPage(nextCursor);
      if (gen !== generation.current) return;
      if (!page.ok) {
        setLoadMoreError(page.error);
      } else {
        setData((prev) => {
          const seen = new Set(prev.map(keyOf));
          return [...prev, ...page.items.filter((row) => !seen.has(keyOf(row)))];
        });
        setNextCursor(page.nextCursor);
      }
    } catch {
      if (gen !== generation.current) return;
      setLoadMoreError({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      if (gen === generation.current) setLoadingMore(false);
    }
  }, [fetchPage, keyOf, nextCursor, loadingMore]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    loading,
    error,
    hasMore: nextCursor !== null,
    loadingMore,
    loadMoreError,
    refresh: load,
    loadMore,
  };
}

/**
 * A single-resource read, with the same guard and the same four fields. Kept
 * separate from `useCursorPage` because a detail read has no cursor and must
 * not expose one.
 */
function useSingleRead<T>(
  read: () => Promise<{ ok: true; data: T } | { ok: false; error: ApiErrorInfo }>,
  /** False when the read is not applicable yet (no id). Settles without a call. */
  enabled: boolean,
): InvoicingQueryResult<T | null> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiErrorInfo | null>(null);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    if (!enabled) {
      // Nothing to scope the read to: not a spinner that can hang, and not a
      // request the gateway has no principal context for.
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await read();
      if (gen !== generation.current) return;
      if (!result.ok) {
        setData(null);
        setError(result.error);
      } else {
        setData(result.data);
      }
    } catch {
      if (gen !== generation.current) return;
      setData(null);
      setError({ reasonCode: NETWORK_ERROR_CODE });
    } finally {
      if (gen === generation.current) setLoading(false);
    }
  }, [read, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, refresh: load };
}

/*
 * Module-level row identities, so they are stable across renders and can be
 * dependencies safely.
 */
const invoiceKey = (row: InvoiceSummary): string => row.invoiceId;
const exceptionKey = (row: Exception): string => row.exceptionId;

/* ---------------------------------------------------------------- invoices */

/**
 * `GET /v1/invoices` — the receivables list, newest first, with cursor paging.
 *
 * `filters` may not carry a `cursor`: paging belongs to this hook, and a page
 * that wants page 2 calls `loadMore`. A change to any filter restarts at page 1
 * and discards an in-flight earlier read.
 */
export function useInvoices(
  filters: Omit<InvoiceListFilters, "cursor"> = {},
): InvoicingPageResult<InvoiceSummary> {
  // Destructured to primitives so the fetcher's identity depends on the filter
  // *values*, not on the caller's object literal — which is new every render.
  const { documentStatus, paymentStatus, payerId, dueFrom, dueTo, limit } = filters;
  const fetchPage = useCallback(
    (cursor: string | null) =>
      listInvoices({
        documentStatus,
        paymentStatus,
        payerId,
        dueFrom,
        dueTo,
        limit,
        cursor: cursor ?? undefined,
      }),
    [documentStatus, paymentStatus, payerId, dueFrom, dueTo, limit],
  );
  return useCursorPage(fetchPage, invoiceKey);
}

/** What one invoice detail read resolves to: the document and its two trails. */
export interface InvoiceDetailData {
  invoice: Invoice;
  payments: Payment[];
  journal: JournalEntry[];
}

/**
 * `GET /v1/invoices/{id}` plus its `/payments` and `/journal` reads — the three
 * a detail page needs, issued in parallel (they are independent; only the id
 * scopes them).
 *
 * **All three or nothing.** A refusal from any of them yields `data: null` and
 * that refusal, even when the others succeeded. Rendering the invoice next to
 * an empty payments list would be a page claiming "no payments" on the strength
 * of a call that never returned 2xx — gotcha 36's shape, where an empty state
 * was only empty because the read had failed. The page has one error state to
 * render and this hook never gives it a half-truth to render instead.
 *
 * Sub-list paging is deliberately out of scope: each trail's first page is
 * loaded (the contract caps the page size), and a page that needs more asks
 * this module for a paged hook rather than being handed a cursor it cannot see
 * the end of.
 */
export function useInvoice(invoiceId: string | undefined): InvoicingQueryResult<InvoiceDetailData | null> {
  const read = useCallback(async () => {
    const [invoice, payments, journal] = await Promise.all([
      getInvoice(invoiceId ?? ""),
      listInvoicePayments(invoiceId ?? ""),
      getInvoiceJournal(invoiceId ?? ""),
    ]);
    // Checked in a fixed order so the reported refusal is deterministic.
    if (!invoice.ok) return { ok: false as const, error: invoice.error };
    if (!payments.ok) return { ok: false as const, error: payments.error };
    if (!journal.ok) return { ok: false as const, error: journal.error };
    return {
      ok: true as const,
      data: { invoice: invoice.data, payments: payments.items, journal: journal.items },
    };
  }, [invoiceId]);

  return useSingleRead<InvoiceDetailData>(read, invoiceId !== undefined && invoiceId !== "");
}

/* ---------------------------------------------------------------- settings */

/**
 * `GET /v1/invoicing/settings` — the org's invoicing configuration.
 *
 * There is no empty state: settings are either readable or refused. A refusal
 * is reported, never rendered as a default configuration, because a fabricated
 * default would show the operator collection wallets and limits the org does
 * not have.
 */
export function useInvoicingSettings(): InvoicingQueryResult<InvoicingSettings | null> {
  const read = useCallback(() => getInvoicingSettings(), []);
  return useSingleRead<InvoicingSettings>(read, true);
}

/* -------------------------------------------------------------- exceptions */

/** `GET /v1/invoice-exceptions` — the exceptions queue, with cursor paging. */
export function useInvoiceExceptions(
  filters: Omit<ExceptionListFilters, "cursor"> = {},
): InvoicingPageResult<Exception> {
  const { state, type, invoiceId, limit } = filters;
  const fetchPage = useCallback(
    (cursor: string | null) =>
      listInvoiceExceptions({ state, type, invoiceId, limit, cursor: cursor ?? undefined }),
    [state, type, invoiceId, limit],
  );
  return useCursorPage(fetchPage, exceptionKey);
}

/* ------------------------------------------------------------- resolutions */

/**
 * The resolutions recorded against one exception, read through
 * `GET /v1/invoice-exceptions/{id}` (see the module header for why this is the
 * only read path the client exposes).
 *
 * `exceptionId` is required for the read to happen; without one the hook
 * settles empty rather than issuing a request with no scope.
 */
export function useInvoiceResolutions(
  exceptionId: string | undefined,
): InvoicingQueryResult<Resolution[] | null> {
  const read = useCallback(async () => {
    const result = await getInvoiceException(exceptionId ?? "");
    if (!result.ok) return { ok: false as const, error: result.error };
    // `resolutions` is optional in the contract, and the detail exists to carry
    // "any resolutions", so an absent field is the service saying there are
    // none — not a shape this hook has to guess at.
    const detail = result.data;
    return {
      ok: true as const,
      data: Array.isArray(detail.resolutions) ? detail.resolutions : [],
    };
  }, [exceptionId]);

  return useSingleRead<Resolution[]>(read, exceptionId !== undefined && exceptionId !== "");
}
